import VueRouter, { Route } from 'vue-router';
import {
	requestAppToken,
	fetchCurrentUser,
	logIn as userApiLogin,
	logOut as userApiLogout,
	updateUserProfile,
	updateUserAvatar,
	activateAccount,
	ActivationStatus,
	updateUserRoles,
} from '@front-app/services/UserApi/UserApi';
import {
	ref,
	Ref,
	computed,
} from '@vue/composition-api';
import {
	NULL_USER_ID,
	getBaseUser,
	FullUser,
	UpdateUserRequest,
	UserDetailRoleNoNone,
} from '@front-app/services/UserApi/UserModel';
import { mergeOccupation } from '@front-app/modules/profile/util/occupation';
import { isCsr } from '@front-app/util/permission';
import { logoutLink } from '@front-app/util/links';
import { API_TOKEN_STORAGE_KEY } from '../user_constants';
import { UserStore } from './user';

export interface CurrentUserStore {
	actions: {
		/**
		 * Loads the current user using a stored appToken if present. Useful during
		 * initial application startup
		 */
		bootstrapUser(): Promise<void>;

		/**
		 * Given an email and password, attempts to log in the user. Returns a promise
		 * that resolves on success.
		 *
		 * On success:
		 * - stores the user's token in localstorage for future use. (See bootstrapUser)
		 * - stores the user in CurrentUserStore.currentUser
		 *
		 * @param email - The user's email address
		 * @param password - The user's password
		 */
		logIn(email: string, password: string): Promise<void>;

		/**
		 * Log a user out of the system. Resolves on success
		 * - Delete the app token from localstorage
		 * - Set CurrentUserStore.currentUser to base user
		 *
		 * Optionally provide a redirect url that will be used post-relogin
		 * if the user logs in again
		 */
		logOut(route: Route, router: VueRouter, url?: string): Promise<void>;

		/**
		 * Given an activation token, actives and loads the current user
		 */
		activate(route: Route, router: VueRouter, token: string): Promise<ActivationStatus>;

		/**
		 * Updates the current user's profile
		 */
		update(updates: Partial<UpdateUserRequest>): Promise<void>;

		/**
		 * Updates the current user's roles
		 */
		updateRoles(roles: UserDetailRoleNoNone[]): Promise<void>;

		updateAvatar(avatarBlob: Blob): Promise<void>;
	};
	currentUser: Ref<FullUser>;
	isLoggedIn: Ref<boolean>;
	isCsr: Ref<boolean>;
}

export const getCurrentUserStore = (userStore: UserStore): CurrentUserStore => {
	const currentUser = ref(getBaseUser());
	const authToken = ref(getStoredAuthToken());
	const isCurrentUserLoggedIn = computed(() => isLoggedIn(currentUser.value));
	const isCurrentUserCsr = computed(() => isCsr(currentUser.value));

	const bootstrapUser = async (): Promise<void> => {
		const [appToken] = await Promise.all([
			requestAppToken(),
			requestUserIfNeeded(authToken, currentUser),
		]);
		/**
		 * The goal is to maintain the same user as is logged in on TR, so if the sessions don't match
		 * we need to invalidate the current auth token.
		 *
		 * If we have a logged in user, we need to check that either they are
		 * a: logged out in their session, in which case it's fine to accept the user OR
		 * b: logged in with their session with the same user as here
		 * */
		if (
			(
				!appToken.apiKey
				|| appToken.apiKey === window.localStorage.getItem(API_TOKEN_STORAGE_KEY)
			)
			&& isLoggedIn(currentUser.value)
		) {
			return Promise.resolve();
		}

		// If we got a valid token but not valid user, all good. Store it, and log in...
		if (appToken.apiKey) {
			setAuthToken(authToken, appToken.apiKey);
			return fetchAndSetCurrentUser(currentUser);
		}
		return Promise.resolve();
	};

	const logIn = (email: string, password: string): Promise<void> => {
		setAuthToken(authToken, null);
		return userApiLogin(email, password)
			.then((newToken) => setAuthToken(authToken, newToken))
			.then(() => fetchAndSetCurrentUser(currentUser));
	};

	const logOut = (route: Route, router: VueRouter, url?: string) => userApiLogout()
		.then(() => {
			setAuthToken(authToken, null);
			currentUser.value = getBaseUser();

			// Redirect to login route if it is set on the current route or any ancestor route
			const loginRoute = [...route.matched].reverse().find((candidateRoute) => candidateRoute.meta?.loginRoute);
			if (loginRoute) {
				const resolvedRoute = loginRoute.meta.loginRoute(route);
				resolvedRoute.query = resolvedRoute.query || {};
				resolvedRoute.query.url = url || route.fullPath;
				router.push(resolvedRoute);
			} else {
				window.location.assign(logoutLink(url));
			}
		});

	const update = async (data: Partial<UpdateUserRequest>) => {
		if (data.currentOccupation) {
			const existing = currentUser.value.currentOccupation;
			// eslint-disable-next-line no-param-reassign
			data = {
				...data,
				currentOccupation: mergeOccupation(data.currentOccupation, existing),
			};
		}
		const result = await updateUserProfile(data);
		currentUser.value = result;
		userStore.setFullUser(result);
	};

	const activate = async (route: Route, router: VueRouter, activationCode: string) => {
		setAuthToken(authToken, null);
		if (isCurrentUserLoggedIn.value) {
			await logOut(route, router);
		}
		const {
			token,
			status,
		} = await activateAccount(activationCode);
		if (status === ActivationStatus.SUCCESS) {
			setAuthToken(authToken, token);
			await fetchAndSetCurrentUser(currentUser);
		}
		return status;
	};

	const updateAvatar = async (avatar: File): Promise<void> => {
		await updateUserAvatar(avatar);
	};

	const updateRoles = async (roles: UserDetailRoleNoNone[]) => {
		const u = currentUser.value;
		const organizationId = u.currentOccupation.employer.id < 0 ? '' : u.currentOccupation.employer.id?.toString();
		const request = {
			organizationId,
			positionTitle: u.currentOccupation.positionTitle,
			startMonth: u.currentOccupation.startMonth?.toString() || '-1',
			startYear: u.currentOccupation.startMonth?.toString() || '-1',
			mobile: u.mobile,
			roles,
		};
		const result = await updateUserRoles(request);
		userStore.setFullUser(result);
		currentUser.value = result;
	};

	return {
		actions: {
			bootstrapUser,
			logIn,
			logOut,
			activate,
			update,
			updateRoles,
			updateAvatar,
		},
		currentUser,
		isLoggedIn: isCurrentUserLoggedIn,
		isCsr: isCurrentUserCsr,
	};
};

function fetchAndSetCurrentUser(currentUser: Ref<FullUser>): Promise<void> {
	return fetchCurrentUser()
		.then((userData) => {
			// eslint-disable-next-line no-param-reassign
			currentUser.value = userData;
		},
		(e) => {
			// eslint-disable-next-line no-console
			console.error('bootstrap user - failed', e);
			return Promise.resolve();
		});
}

function isLoggedIn(user: FullUser): boolean {
	return Boolean(user.id) && user.id !== NULL_USER_ID;
}

function getStoredAuthToken(): string | null {
	return localStorage.getItem(API_TOKEN_STORAGE_KEY);
}

type AuthToken = Ref<string | null>;

function setAuthToken(authToken: AuthToken, newToken: string | null): void {
	if (newToken === null) {
		window.localStorage.removeItem(API_TOKEN_STORAGE_KEY);
	} else {
		// eslint-disable-next-line no-param-reassign
		authToken.value = window.btoa(`${window.atob(newToken)}:`);
		window.localStorage.setItem(API_TOKEN_STORAGE_KEY, authToken.value);
	}
}

function requestUserIfNeeded(authToken: AuthToken, currentUser: Ref<FullUser>): Promise<void> {
	if (authToken.value === null) {
		return Promise.resolve();
	}
	return fetchAndSetCurrentUser(currentUser);
}
